import React, { MouseEventHandler, Ref, useRef, useState, WheelEventHandler } from 'react'
import { isDesktop } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { SafeHtml } from '../SafeHtml'
import { useStaticText, DescriptionItem } from '../../contexts/staticTextContext'
import { useContextPanels } from '../../contexts/panelsContext'
import { useEnv } from '../../utils/useEnv'
import CloseButton from '../CloseButton'
import { UIElement } from '../../types/UIElements'
import { cn } from '../../utils/cn'

const Gallery = () => {
  const { t, i18n } = useTranslation()
  const { isKiosk } = useEnv()
  const { galleryData } = useStaticText()
  const { closePanel } = useContextPanels()
  const containerRef = useRef<HTMLDivElement>(null)
  const [wrapperClass, setWrapperClass] = useState<string>('fade-in')
  const [fadeOut, setFadeOut] = useState<boolean>(false)

  const lang = i18n.language

  const handleHorizontalWheelScroll: WheelEventHandler<HTMLDivElement> = event => {
    if (!containerRef?.current) return
    event.stopPropagation()
    containerRef.current.scrollLeft += event.deltaY
  }

  const handleClickOutside: MouseEventHandler<HTMLDivElement> = e => {
    if (e.target !== containerRef.current) return
    handleClose()
  }

  const handleClose = () => {
    setWrapperClass('fade-out')
    setFadeOut(true)
    window.history.replaceState(null, '', '/')
    setTimeout(() => {
      closePanel(UIElement.GALLERY)
      setFadeOut(false)
    }, 500)
  }

  return (
    <>
      <div
        ref={containerRef as Ref<HTMLDivElement>}
        onWheel={isDesktop ? handleHorizontalWheelScroll : undefined}
        onClick={handleClickOutside}
        className={cn(
          'fixed top-0 right-0 max-h-full overflow-x-hidden overflow-y-auto flex flex-col items-center gap-[23px] opacity-0 [transform:translateX(50px)] [&.fade-in]:animate-[fade-in-x50_0.5s_ease_forwards] [&.fade-out]:animate-[fade-out-x50_0.5s_ease_forwards] max-sm:short-sm:w-[calc(100vw-82px)] max-sm:short-sm:pl-[8px] max-sm:short-sm:pr-[90px] sm:flex-row sm:left-0 sm:w-screen sm:min-h-fill-available sm:pl-[172px] sm:overflow-x-auto sm:overflow-y-hidden sm:gap-[46px] xxl:pl-[10vw]',
          wrapperClass,
        )}
      >
        <div className="mb-[100px] ml-0 mr-0 mt-0 h-full w-full text-[12px] tall-sm:mb-0 tall-sm:ml-0 tall-sm:mr-[50px] tall-sm:mt-0 tall-sm:w-[unset] tall-sm:text-[16px] sm:min-h-[calc(100vh-2*10vh)] sm:text-[14px] sm:tall-sm:text-[16px] xxl:flex xxl:items-center">
          <div
            className={cn(
              'flex h-full w-full flex-col gap-[30px] rounded-[24px] bg-white pb-[32px] pl-[32px] pr-[32px] pt-[64px] opacity-0 shadow-[0_2px_4px_0_rgba(0,0,0,0.2),0_4px_10px_0_rgba(0,0,0,0.2)] [align-items:start] [transform:translateY(30px)] tall-sm:h-[700px] tall-sm:gap-[40px] tall-sm:pb-[50px] tall-sm:pl-[40px] tall-sm:pr-[40px] tall-sm:pt-[100px] tall-lg:h-[850px] sm:flex-row [&.fade-in]:animate-[fade-in-y30_0.3s_0.3s_ease_forwards] [&.fade-out]:animate-[fade-out-y30_0.3s_ease_forwards]',
              { 'fade-out': fadeOut, 'fade-in': !fadeOut },
            )}
          >
            {galleryData.map((gallery, galleryIndex) => (
              <React.Fragment key={galleryIndex}>
                {(gallery[`description_${lang}` as keyof typeof gallery] as DescriptionItem[])
                  ?.length > 0 && (
                  <div
                    key={galleryIndex}
                    className={cn('tall-sm:w-[500px]', {
                      'text-[16px] leading-[1.5] tracking-[0.38px] text-black49 tall-sm:text-[21px] tall-sm:tracking-[0.5px]':
                        galleryIndex !== 0,
                    })}
                  >
                    <main>
                      {galleryIndex === 0 && (
                        <>
                          <h1 className="font-approach-light text-[28px] leading-[1.375] tracking-[0.35px] text-grey40 tall-sm:text-[32px]">
                            {t('gallery.title')}
                          </h1>
                          <h2 className="mb-[0.6rem] font-approach-light text-[21px] leading-[1.5] tracking-[0.29px] text-grey40 tall-sm:text-[24px]">
                            {(gallery[`title_${lang}` as keyof typeof gallery] as string) ||
                              '\u00A0'}
                          </h2>
                        </>
                      )}
                      {galleryIndex !== 0 && (
                        <>
                          <h1 className="font-approach-light text-[28px] leading-[1.375] tracking-[0.35px] text-grey40 tall-sm:text-[32px]">
                            {(gallery[`title_${lang}` as keyof typeof gallery] as string) ||
                              '\u00A0'}
                          </h1>

                          <h2 className="mb-[0.6rem] font-approach-light text-[21px] leading-[1.5] tracking-[0.29px] text-grey40 tall-sm:text-[24px]">
                            {(
                              gallery[
                                `description_${lang}` as keyof typeof gallery
                              ] as DescriptionItem[]
                            )?.[0]?.notes?.toLowerCase() === 'h'
                              ? (
                                  gallery[
                                    `description_${lang}` as keyof typeof gallery
                                  ] as DescriptionItem[]
                                )?.[0]?.text
                              : '\u00A0'}
                          </h2>
                        </>
                      )}
                      {(
                        gallery[`description_${lang}` as keyof typeof gallery] as DescriptionItem[]
                      )?.map((item, index) => {
                        if (index === 0 && item.notes?.toLowerCase() === 'h') return null
                        const textWithBreaks = item.text?.replace(/\n/g, '<br />')
                        return (
                          <SafeHtml
                            key={index}
                            html={textWithBreaks || ''}
                            allowedTags={['a', 'b', 'br']}
                            allowedAttrs={['href']}
                            disableLinks={isKiosk}
                            className={cn(
                              'mb-[1.375rem] font-approach-light text-[16px] leading-[1.375] tracking-[0.38px] text-grey40 tall-sm:text-[18px]',
                              { addMargin: item.notes?.toLowerCase() === 'm' },
                            )}
                          />
                        )
                      })}
                    </main>
                  </div>
                )}
                {gallery.multimedia && gallery.multimedia.length > 0 && (
                  <div className="flex [flex-direction:inherit] tall-sm:h-full max-sm:items-center max-sm:justify-center">
                    {gallery.multimedia.map((media, mediaIndex) => (
                      <img
                        key={mediaIndex}
                        className="w-full tall-sm:h-full tall-sm:w-auto max-sm:h-auto max-sm:w-auto max-sm:max-w-full max-sm:tall-sm:h-full max-sm:tall-sm:w-auto"
                        src={media.multimediaUri}
                        alt="generated pattern"
                      />
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      <div className="min-h-fill-available absolute right-[8px] top-[18px] flex items-center justify-start overflow-y-hidden sm:right-0 sm:top-0 sm:w-[150px] [&_svg]:h-[24px] [&_svg]:w-[24px] sm:[&_svg]:h-[30px] sm:[&_svg]:w-[30px]">
        <CloseButton onClick={handleClose} className={wrapperClass} />
      </div>
    </>
  )
}

export default Gallery
